export const fmtUsd = (n: number | undefined | null): string =>
  n == null ? '—' : n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`

export const fmtTokens = (n: number | undefined | null): string => {
  if (n == null) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

export const fmtDuration = (ms: number | undefined | null): string => {
  if (ms == null) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  return `${m}m ${Math.round(s % 60)}s`
}

export const fmtRelative = (epochSeconds: number): string => {
  const d = Date.now() / 1000 - epochSeconds
  if (d < 60) return 'just now'
  if (d < 3600) return `${Math.floor(d / 60)}m ago`
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`
  return `${Math.floor(d / 86400)}d ago`
}

export const basename = (p: string): string =>
  p.replace(/\/+$/, '').split('/').pop() ?? p

export const shortPath = (p: string, cwd?: string | null): string => {
  if (cwd && p.startsWith(cwd + '/')) return p.slice(cwd.length + 1)
  return p
}

export const truncate = (s: string, n: number): string =>
  s.length > n ? s.slice(0, n - 1) + '…' : s

type KnownModel = { id: string; label: string; aliases?: string[] }

/** Resolve a CLI-reported model id to a catalog entry (exact id, alias, or dated snapshot of it). */
export function findModel<T extends KnownModel>(
  id: string | null | undefined,
  models: readonly T[],
): T | undefined {
  if (!id) return undefined
  return (
    models.find((m) => m.id === id || m.aliases?.includes(id)) ??
    models.find(
      (m) =>
        id.startsWith(m.id + '-') ||
        m.aliases?.some((a) => id.startsWith(a + '-')),
    )
  )
}

/** "claude-haiku-4-5-20251001" -> "Haiku 4.5", "claude-fable-5-1" -> "Fable 5.1", "opus" -> "Opus". */
export function modelLabel(
  id: string | null | undefined,
  models: readonly KnownModel[] = [],
): string {
  if (!id) return '—'
  const known = findModel(id, models)
  if (known) return known.label
  const parts = id
    .replace(/^claude-/, '')
    .replace(/-\d{8}$/, '')
    .split('-')
  const family = parts[0] ?? id
  const version = parts
    .slice(1)
    .filter((p) => /^\d+$/.test(p))
    .join('.')
  const rest = parts
    .slice(1)
    .filter((p) => !/^\d+$/.test(p))
    .join(' ')
  return [family.charAt(0).toUpperCase() + family.slice(1), version, rest]
    .filter(Boolean)
    .join(' ')
}

export const fmtTime = (d: Date | undefined): string =>
  d
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : ''
